import { useEffect, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@eureca/ui';
import { useDebouncedValue } from '@eureca/ui/forms';
import { toFriendlyMessage } from '@/lib/api-error';
import { WorkspaceBrand } from '@/shared/workspace/WorkspaceBrand';
import { ProgramsPage as ProgramsPageComponent } from './ProgramsPage.component';
import type { ProgramAction, ProgramActiveDialog } from './ProgramsPage.component';
import type { PublishVisibility } from './ProgramPublishDialog';
import { programsQueryOptions, type ProgramListEntry } from './programs-query';
import { mapProgramRow } from './map-program-row';
import { PAGE_SIZE_OPTIONS, type PageSize, type ProgramsSearch } from './programs-search.schema';
import {
  applySortChange,
  clearSortChange,
  selectedSortOption,
  sortOptionId,
  SORT_OPTIONS,
  type SortOption,
} from './programs-state';
import {
  ProgramMutationError,
  useCancelProgram,
  useDuplicateProgram,
  useEndProgram,
  usePublishProgram,
} from '../program-edit/program-mutations';
import {
  PUBLISH_INCOMPLETE_JOBS_MESSAGE,
  usePublishReadiness,
} from '../program-edit/publish-readiness-query';
import { useExportProgramBase } from '../program-export/use-export-program-base';
import { programExportAvailabilityQueryOptions } from '../program-export/program-export-availability';
import { baseTypeForExportAction, bulkExportTypeForAction } from '../program-actions-menu';
import { useBulkDocumentExport } from '../program-export/use-bulk-document-export';
import { recruiterEnv } from '@/config/env';
import { buildCandidateProgramUrl, copyPrivateLink, openPrivateLink } from '@/lib/candidate-links';

const SEARCH_DEBOUNCE_MS = 300;

export default function ProgramsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search = useSearch({ from: '/_authenticated/programas' }) as ProgramsSearch;
  const duplicateProgram = useDuplicateProgram();
  const cancelProgram = useCancelProgram();
  const endProgram = useEndProgram();
  const publishProgram = usePublishProgram();
  const exportProgramBase = useExportProgramBase();
  // DEV-498 — exportação em massa (assíncrona; 4 toasts; resultado por e-mail).
  const bulkDocumentExport = useBulkDocumentExport();

  // EU-654 — disponibilidade dos tipos de base (RN-02/03): buscada LAZY quando
  // o menu de uma linha abre; um menu por vez, cache por programa (queryKey).
  const [exportMenuProgramId, setExportMenuProgramId] = useState<string | null>(null);
  const exportAvailability = useQuery({
    ...programExportAvailabilityQueryOptions(exportMenuProgramId ?? ''),
    enabled: exportMenuProgramId !== null,
  });

  const query = useQuery(programsQueryOptions(search));

  const [rawSearchInput, setRawSearchInput] = useState(search.search ?? '');
  const debouncedSearch = useDebouncedValue(rawSearchInput, SEARCH_DEBOUNCE_MS);

  const [activeDialog, setActiveDialog] = useState<ProgramActiveDialog>(null);
  // The id of the program targeted by the open dialog. Used by status-transition
  // mutations (cancel today; publish/end in a follow-up). Cleared on dialog close.
  const [targetProgramId, setTargetProgramId] = useState<string | null>(null);
  const [publishVisibility, setPublishVisibility] = useState<PublishVisibility>('public');

  // EU-450 — pre-check on publish-dialog open: blocks Publicar + lists the
  // incomplete jobs (with links) while any linked vaga misses required
  // sections. Backend (Core PATCH gate) stays the authority; this only
  // reflects it upfront (RN-04).
  const publishReadiness = usePublishReadiness(targetProgramId, activeDialog === 'publish');
  const publishBlocked = activeDialog === 'publish' && publishReadiness.data?.ready === false;
  const publishError = publishBlocked ? PUBLISH_INCOMPLETE_JOBS_MESSAGE : undefined;
  const publishIncompleteJobs = publishBlocked
    ? publishReadiness.data?.incompleteJobs.map(({ id, name }) => ({ id, name }))
    : undefined;
  const isCheckingPublishReadiness = activeDialog === 'publish' && publishReadiness.isLoading;

  // Push debounced search input into the URL — URL is the canonical state.
  useEffect(() => {
    const next = debouncedSearch.trim().length >= 2 ? debouncedSearch.trim() : undefined;
    if (next === search.search) return;
    void navigate({
      to: '/programas',
      search: (prev) => ({ ...(prev as ProgramsSearch), search: next, page: 1 }),
    });
  }, [debouncedSearch, search.search, navigate]);

  const items: ProgramListEntry[] = query.data?.items ?? [];
  const programs = items.map(mapProgramRow);
  const total = query.data?.total ?? 0;
  const pageSize = query.data?.pageSize ?? search.pageSize;
  const page = query.data?.page ?? search.page;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Clamp a stale `page` URL param down to the last valid page after the
  // dataset shrinks (cancel/end/delete on the last row of the last page, or
  // a filter that narrows total below the current offset). Without this the
  // BFF returns `items: []` and the page renders EmptyState even though
  // there are programs on lower pages.
  useEffect(() => {
    if (!query.data) return;
    if (search.page <= totalPages) return;
    void navigate({
      to: '/programas',
      search: (prev) => ({ ...(prev as ProgramsSearch), page: totalPages }),
    });
  }, [query.data, search.page, totalPages, navigate]);

  // Cold load (no cache, no placeholder data) → "Buscando programas…".
  // Refetch with prior data visible (sort/search/page change) → "Reordenando programas…".
  const isInitialLoading = query.isPending && !query.data;
  const isReordering = query.isFetching && query.isPlaceholderData;

  function handleSearchChange(value: string) {
    setRawSearchInput(value);
  }

  function handleRowsPerPageChange(rows: number) {
    const next = (PAGE_SIZE_OPTIONS as readonly number[]).includes(rows) ? (rows as PageSize) : 10;
    void navigate({
      to: '/programas',
      search: (prev) => ({ ...(prev as ProgramsSearch), pageSize: next, page: 1 }),
    });
  }

  function handleSelectSort(id: string) {
    const opt = SORT_OPTIONS.find((o) => sortOptionId(o.sort, o.dir) === id);
    if (!opt) return;
    void navigate({
      to: '/programas',
      search: (prev) => applySortChange(prev as ProgramsSearch, opt),
    });
  }

  function handleClearSort() {
    void navigate({
      to: '/programas',
      search: (prev) => clearSortChange(prev as ProgramsSearch),
    });
  }

  function handlePageChange(nextPage: number) {
    void navigate({
      to: '/programas',
      search: (prev) => ({ ...(prev as ProgramsSearch), page: nextPage }),
    });
  }

  function handleCreateProgram() {
    void navigate({ to: '/programas/novo' });
  }

  function handleOpenProgram(id: string) {
    // EU-593 — o clique no programa abre o painel "Gerenciar programa"; o
    // formulário (edição EU-570 / visualização) fica atrás do botão
    // Editar/Conferir informações dentro do painel.
    void navigate({
      to: '/programas/$programId',
      params: { programId: id },
    });
  }

  function handleProgramAction(action: ProgramAction, id: string) {
    setTargetProgramId(id);
    // EU-561 + EU-654 — "Extrair bases": baixa direto, com toast de 3 estados
    // (Preparando base → baixada | erro), sem tela nem modal (RN-06).
    const baseType = baseTypeForExportAction(action);
    if (baseType) {
      exportProgramBase.mutate({ programId: id, type: baseType });
      return;
    }
    // DEV-498 — "Currículos em massa" / "Laudos em massa": nada baixa; o Core
    // responde na hora (toast) e o resultado chega por e-mail.
    const bulkType = bulkExportTypeForAction(action);
    if (bulkType) {
      bulkDocumentExport.mutate({ programId: id, type: bulkType });
      return;
    }
    if (action === 'publish') {
      setPublishVisibility('public');
      setActiveDialog('publish');
      return;
    }
    if (action === 'end') {
      setActiveDialog('end');
      return;
    }
    if (action === 'cancel') {
      setActiveDialog('cancel');
      return;
    }
    if (action === 'edit') {
      void navigate({
        to: '/programas/$programId/editar',
        params: { programId: id },
      });
      return;
    }
    if (action === 'open-private-link') {
      openPrivateLink(buildCandidateProgramUrl(recruiterEnv.candidateAppUrl, id));
      return;
    }
    if (action === 'copy-private-link') {
      void copyPrivateLink(
        buildCandidateProgramUrl(recruiterEnv.candidateAppUrl, id),
        'Link privado do programa'
      );
      return;
    }
    if (action === 'duplicate') {
      void duplicateProgram
        .mutateAsync(id)
        .then((newProgram) => {
          void navigate({
            to: '/programas/$programId/editar',
            params: { programId: newProgram.id },
            search: { copy: true },
          });
        })
        .catch((err: unknown) => {
          // Surface the BFF's canonical wording only for the duplicate
          // active-program-name 409; every other failure gets a friendly,
          // non-technical message (EU-403 RN-01/RN-02).
          const message =
            err instanceof ProgramMutationError && err.code === 'duplicate_program_name'
              ? err.message
              : toFriendlyMessage(err);
          toast(message, { action: { label: 'Fechar', onClick: () => undefined } });
        });
      return;
    }
    if (action === 'make-private' || action === 'make-public') {
      // "Privar" / "Tornar público" (EU-522 RN-02) — alternam a visibilidade de
      // um programa JÁ publicado, sem abrir o diálogo de publicação (o consultor
      // já escolheu a ação explicitamente). São ações inversas e compartilham a
      // mesma mutation; só a visibilidade e o toast mudam.
      const visibility = action === 'make-private' ? 'private' : 'public';
      publishProgram.mutate(
        { programId: id, visibility },
        {
          onSuccess: () => {
            refreshList();
            toast(visibility === 'private' ? 'Programa privado' : 'Programa público', {
              action: { label: 'Fechar', onClick: () => undefined },
            });
          },
          onError: (err) => {
            // The duplicate active-program-name 409 also fires when a draft
            // joins the active pool — surface its canonical wording; anything
            // else gets a friendly message (EU-403 RN-01/RN-02).
            const message =
              err instanceof ProgramMutationError && err.code === 'duplicate_program_name'
                ? err.message
                : toFriendlyMessage(err);
            toast(message, { action: { label: 'Fechar', onClick: () => undefined } });
          },
        }
      );
      return;
    }
  }

  function handleDialogOpenChange(open: boolean) {
    if (!open) {
      setActiveDialog(null);
      setTargetProgramId(null);
    }
  }

  function refreshList() {
    // Invalidate every cached page (`programsQueryKey` is paginated). After a
    // status mutation any page may be stale, not just the one currently shown.
    void queryClient.invalidateQueries({ queryKey: ['recruiter', 'programs'] });
  }

  function handleConfirmPublish() {
    if (!targetProgramId) {
      handleDialogOpenChange(false);
      return;
    }
    const id = targetProgramId;
    const visibility = publishVisibility;
    handleDialogOpenChange(false);
    publishProgram.mutate(
      { programId: id, visibility },
      {
        onSuccess: () => {
          refreshList();
          toast('Programa publicado', {
            action: { label: 'Fechar', onClick: () => undefined },
          });
        },
        onError: (err) => {
          // Surface the canonical wording of typed 4xx codes (duplicate name
          // 409; EU-450 incomplete-jobs 422 — race where a vaga regressed
          // between the dialog pre-check and the PATCH); every other failure
          // gets a friendly message (EU-403 RN-01/RN-02).
          const message =
            err instanceof ProgramMutationError && err.code !== null
              ? err.message
              : toFriendlyMessage(err);
          toast(message, { action: { label: 'Fechar', onClick: () => undefined } });
        },
      }
    );
  }

  function handleConfirmEnd() {
    if (!targetProgramId) {
      handleDialogOpenChange(false);
      return;
    }
    const id = targetProgramId;
    handleDialogOpenChange(false);
    endProgram.mutate(id, {
      onSuccess: () => {
        refreshList();
        toast('Programa encerrado', {
          action: { label: 'Fechar', onClick: () => undefined },
        });
      },
      onError: () => {
        toast('Não foi possível encerrar o programa', {
          action: { label: 'Fechar', onClick: () => undefined },
        });
      },
    });
  }

  function handleConfirmCancel() {
    if (!targetProgramId) {
      handleDialogOpenChange(false);
      return;
    }
    const id = targetProgramId;
    handleDialogOpenChange(false);
    cancelProgram.mutate(id, {
      onSuccess: () => {
        refreshList();
        toast('Programa cancelado', {
          action: { label: 'Fechar', onClick: () => undefined },
        });
      },
      onError: () => {
        toast('Não foi possível cancelar o programa', {
          action: { label: 'Fechar', onClick: () => undefined },
        });
      },
    });
  }

  const selectedSort: SortOption | undefined = selectedSortOption(search);
  const sortOptions = SORT_OPTIONS.map((o) => ({
    id: sortOptionId(o.sort, o.dir),
    label: o.label,
    isSelected: selectedSort === o,
  }));

  return (
    <ProgramsPageComponent
      programs={programs}
      brand={search.companyId ? <WorkspaceBrand companyId={search.companyId} /> : undefined}
      isInitialLoading={isInitialLoading}
      isReordering={isReordering}
      searchQuery={rawSearchInput}
      onSearchChange={handleSearchChange}
      onCreateProgram={handleCreateProgram}
      sortOptions={sortOptions}
      selectedSortLabel={selectedSort?.label}
      isSortSelected={selectedSort !== undefined}
      onSelectSort={handleSelectSort}
      onClearSort={handleClearSort}
      onOpenProgram={handleOpenProgram}
      onProgramAction={handleProgramAction}
      exportMenuProgramId={exportMenuProgramId}
      exportMenuTypes={exportAvailability.data}
      onRowMenuOpenChange={(programId, open) => {
        // EU-654 — o fechar NÃO zera o id: os itens do menu disparam onClick
        // depois do onOpenChange(false), e o Popover desmonta em seguida.
        if (open) setExportMenuProgramId(programId);
      }}
      rowsPerPage={pageSize}
      rowsPerPageOptions={[...PAGE_SIZE_OPTIONS]}
      onRowsPerPageChange={handleRowsPerPageChange}
      page={page}
      totalPages={totalPages}
      onPageChange={handlePageChange}
      activeDialog={activeDialog}
      onDialogOpenChange={handleDialogOpenChange}
      publishVisibility={publishVisibility}
      onPublishVisibilityChange={setPublishVisibility}
      onConfirmPublish={handleConfirmPublish}
      onConfirmEnd={handleConfirmEnd}
      onConfirmCancel={handleConfirmCancel}
      publishError={publishError}
      publishIncompleteJobs={publishIncompleteJobs}
      isCheckingPublishReadiness={isCheckingPublishReadiness}
    />
  );
}
